import React, { useCallback, useState } from 'react';
import { Box, Button, Grid2 as Grid } from '@mui/material';
import { ShowPageLayout } from 'framework/ui/layout';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';
import useLeaveBalanceService from 'domain/people/employee/leavebalance/shared/balancesService';
import AccordionSection from '../../shared/AccrodionSection';
import ProcessTimelineComponent from '../../shared/ProcessTimelineComponent';
import { useProcesses } from '../../shared/ProcessesContext';
import UpcomingLeavesContainer from '../upcoming/UpcomingLeaves';
import MyLeavesCharts from '../charts/MyLeavesCharts';
import LeavesDetailsModal from './LeavesDetailsModal';
import { ShowPortalLeaveType } from './type';
import { Show, useShowContext } from 'framework/show';
import { isEmployeeContractByType, isPolishOffice } from 'domain/shared/employee';

const LeavesSection = React.memo(() => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { getPaidLeavesBalances, getSickDaysBalances } = useLeaveBalanceService();
  const { getUserAlias } = useAuthenticatedUserService();
  const { data: employeeData } = useShowContext();
  const { hrProcesses, loading: processesLoading, error: processesError } = useProcesses();

  const getData = useCallback(async (): Promise<ShowPortalLeaveType | {}> => {
    const alias = await getUserAlias();
    if (!alias) return {};

    const [paidBalances, sickBalances] = await Promise.all([getPaidLeavesBalances(alias), getSickDaysBalances(alias)]);

    const employee = employeeData?.getData?.() || {};

    const hasSickDaysBenefit = !(
      employee &&
      isPolishOffice(String(employee.officeId)) &&
      isEmployeeContractByType(String(employee.contractType))
    );

    return { paidBalances, sickBalances, currentDate: new Date(), userId: alias, hasSickDaysBenefit };
  }, [employeeData, getPaidLeavesBalances, getSickDaysBalances, getUserAlias]);

  const handleModalToggle = useCallback((open: boolean) => {
    setIsModalOpen(open);
  }, []);

  return (
    <Box sx={{ '& > .MuiPaper-root': { minHeight: 'unset' } }}>
      <ShowPageLayout minHeight="100px">
        <Show getDataMethod={getData}>
          <AccordionSection
            title="My leaves"
            defaultExpanded
            actions={
              <Button
                variant="outlined"
                onClick={e => {
                  e.stopPropagation();
                  handleModalToggle(true);
                }}
                size="small"
              >
                View details
              </Button>
            }
          >
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={3} gap={4}>
                <Grid size={12}>
                  <MyLeavesCharts />
                </Grid>
                <Grid container spacing={2} wrap="nowrap" size={12}>
                  <Grid size={{ xs: 6, sm: 6 }} sx={{ mx: 3 }}>
                    <UpcomingLeavesContainer />
                  </Grid>
                  {!!hrProcesses.length && (
                    <Grid size={6} sx={{ mx: 3 }}>
                      <ProcessTimelineComponent
                        processes={hrProcesses}
                        loading={processesLoading}
                        error={processesError}
                        title="Active requests"
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Box>
          </AccordionSection>
        </Show>

        <LeavesDetailsModal open={isModalOpen} onClose={() => handleModalToggle(false)} />
      </ShowPageLayout>
    </Box>
  );
});

export default LeavesSection;
